// SpriteUpload.jsx
// Component for uploading sprite images and configuring species sprite settings

import React, { useState } from "react";
import { getStorage, ref, uploadBytes } from "firebase/storage";
import { updateDB } from "../../DatabaseOperations";
import ConsoleHomePageHeader from "../Home/ConsoleHomePageHeader";

// CSS
import "./SpriteUpload.css";

const createDefaultStages = () =>
  Array.from({ length: 3 }, () => ({
    height: 200,
    overlap: 0,
    size: "medium",
    image: null,
    imageName: "",
  }));

const SpriteUpload = () => {
  // Form state
  const [speciesName, setSpeciesName] = useState("");

  // Stage configurations
  const [stages, setStages] = useState(createDefaultStages());

  // UI state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Handle file selection for a specific stage
  const handleImageSelect = (stageIndex, file) => {
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setError(`Stage ${stageIndex}: Please select an image file`);
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError(
          `Stage ${stageIndex}: Image file is too large. Maximum size is 5MB`
        );
        return;
      }

      // Validate file extension
      if (!file.name.toLowerCase().endsWith(".png")) {
        setError(`Stage ${stageIndex}: Please select a PNG file`);
        return;
      }

      const newStages = [...stages];
      newStages[stageIndex].image = file;
      newStages[stageIndex].imageName = file.name;
      setStages(newStages);
      setError(""); // Clear any previous errors
    }
  };

  // Handle stage configuration changes
  const handleStageChange = (stageIndex, field, value) => {
    const newStages = [...stages];
    newStages[stageIndex][field] = value;
    setStages(newStages);
  };

  // Upload images to Firebase Storage
  const uploadImages = async () => {
    const storage = getStorage();
    const uploadPromises = [];
    const newUploadProgress = {};

    stages.forEach((stage, index) => {
      if (stage.image) {
        newUploadProgress[index] = 0;
        const imageRef = ref(
          storage,
          `spriteComponents/plants/${stage.imageName}`
        );
        uploadPromises.push(
          uploadBytes(imageRef, stage.image).then(() => {
            newUploadProgress[index] = 100;
            setUploadProgress({ ...newUploadProgress });
          })
        );
      }
    });

    await Promise.all(uploadPromises);
    return uploadPromises.length;
  };

  // Update species document in Firestore
  const updateSpeciesDocument = async () => {
    const stagesConfig = stages.map((stage) => ({
      plantBody: {
        height: Number(stage.height),
        imageName: stage.imageName,
        x: 0,
        y: 0,
      },
      plantPotOverlap: Number(stage.overlap),
      size: stage.size,
    }));

    await updateDB(speciesName, "Species", {
      stages: stagesConfig,
    });
  };

  // Main upload function
  const handleUpload = async () => {
    // Clear previous errors
    setError("");

    // Validate required fields
    if (!speciesName.trim()) {
      setError("Species name is required");
      return;
    }

    // Validate that at least one image is selected
    const hasImages = stages.some((stage) => stage.image);
    if (!hasImages) {
      setError("Please select at least one image to upload");
      return;
    }

    // Validate that all selected images have proper configuration
    const invalidStages = stages
      .map((stage, index) => ({ ...stage, index }))
      .filter(
        (stage) =>
          stage.image &&
          (!stage.height || stage.height < 100 || stage.height > 600)
      );

    if (invalidStages.length > 0) {
      setError(
        `Stage ${invalidStages[0].index}: Height must be between 100 and 600 pixels`
      );
      return;
    }

    // Validate overlap values
    const invalidOverlaps = stages
      .map((stage, index) => ({ ...stage, index }))
      .filter(
        (stage) => stage.image && (stage.overlap < 0 || stage.overlap > 1)
      );

    if (invalidOverlaps.length > 0) {
      setError(
        `Stage ${invalidOverlaps[0].index}: Overlap must be between 0 and 1`
      );
      return;
    }

    setIsUploading(true);
    setResult(null);

    try {
      // Upload images first
      const uploadedCount = await uploadImages();

      // Update species document
      await updateSpeciesDocument();

      setResult({
        message: `Successfully uploaded ${uploadedCount} images and updated species configuration`,
        speciesName,
        stages: stages.map((stage) => ({
          imageName: stage.imageName,
          height: stage.height,
          overlap: stage.overlap,
          size: stage.size,
        })),
      });

      // Reset form
      setSpeciesName("");
      setStages(createDefaultStages());
      setUploadProgress({});
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
      console.error("Upload error:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setSpeciesName("");
    setStages(createDefaultStages());
    setUploadProgress({});
    setResult(null);
    setError("");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ConsoleHomePageHeader />

      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">
            Sprite Upload
          </h1>

          {/* Basic Species Information */}
          <div className="grid grid-cols-1 gap-6 mb-8">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Species Name *
              </label>
              <input
                type="text"
                value={speciesName}
                onChange={(e) => setSpeciesName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="e.g., Dracaena trifasciata"
              />
            </div>
          </div>

          {/* Stage Configuration */}
          <div className="space-y-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-800">
              Stage Configuration
            </h2>

            {stages.map((stage, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4"
              >
                <h3 className="text-lg font-medium text-gray-700 mb-4">
                  Stage {index}{" "}
                  {index === 0
                    ? "(Seedling)"
                    : index === 1
                    ? "(Growing)"
                    : "(Mature)"}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Image Upload */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Plant Body Image *
                    </label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handleImageSelect(index, e.target.files[0])
                        }
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                      />
                      {stage.image && (
                        <div className="flex-shrink-0">
                          <img
                            src={URL.createObjectURL(stage.image)}
                            alt={`Stage ${index} preview`}
                            className="w-16 h-16 object-cover rounded border"
                          />
                        </div>
                      )}
                    </div>
                    {stage.imageName && (
                      <p className="text-xs text-gray-500 mt-1">
                        Will be saved as: {stage.imageName}
                      </p>
                    )}
                    {uploadProgress[index] !== undefined && (
                      <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress[index]}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-600">
                          {uploadProgress[index]}%
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Height */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Height (px)
                    </label>
                    <input
                      type="number"
                      value={stage.height}
                      onChange={(e) =>
                        handleStageChange(index, "height", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      min="100"
                      max="600"
                    />
                  </div>

                  {/* Overlap */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pot Overlap
                    </label>
                    <input
                      type="number"
                      value={stage.overlap}
                      onChange={(e) =>
                        handleStageChange(index, "overlap", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      min="0"
                      max="1"
                      step="0.05"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {stage.overlap * 100}% overlap
                    </p>
                  </div>

                  {/* Size */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Size
                    </label>
                    <select
                      value={stage.size}
                      onChange={(e) =>
                        handleStageChange(index, "size", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="small">Small</option>
                      <option value="medium">Medium</option>
                      <option value="large">Large</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4">
            <button
              onClick={handleUpload}
              disabled={isUploading}
              className="flex-1 bg-green-600 text-white px-6 py-3 rounded-md font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isUploading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Uploading...
                </>
              ) : (
                "Upload Sprite & Update Species"
              )}
            </button>

            <button
              onClick={resetForm}
              disabled={isUploading}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reset
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Success Display */}
          {result && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-md">
              <h3 className="text-green-800 font-medium mb-2">
                Upload Successful!
              </h3>
              <p className="text-green-700 mb-3">{result.message}</p>
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-gray-800 mb-2">
                  Configuration Summary:
                </h4>
                <div className="space-y-2">
                  <p>
                    <span className="font-medium">Species:</span>{" "}
                    {result.speciesName}
                  </p>
                  {result.stages.map((stage, index) => (
                    <div key={index} className="text-sm">
                      <span className="font-medium">Stage {index}:</span>{" "}
                      {stage.imageName}({stage.height}px, {stage.overlap * 100}%
                      overlap, {stage.size})
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Help Information */}
          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <h3 className="text-blue-800 font-medium mb-2">
              Help & Guidelines
            </h3>
            <ul className="text-blue-700 text-sm space-y-1">
              <li>
                • Images should be PNG format with transparent backgrounds
              </li>
              <li>• Recommended dimensions: 600x450px (WxH)</li>
              <li>• Height values affect pot scaling and positioning</li>
              <li>
                • Overlap values (0-1) determine how much plant overlaps the pot
              </li>
              <li>
                • Size affects pot width: small (50%), medium (75%), large
                (100%)
              </li>
              <li>• Uploaded image file names are preserved as-is</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpriteUpload;
